import { useMutation } from "@apollo/client";
import { weightedAveragesQuery } from "../WeightedAverages";
import { supplyChainLotQuery, type SupplyChainLot } from "./index";
import { LotForm } from "@/benchmarks/LotForm";
import { type LotFormSchema } from "@/benchmarks/LotForm/validation";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { graphql } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { nullable } from "@/lib/utils/common";
import { toISO, toJsDate } from "@/lib/utils/dates";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import { transformForApi } from "@/lib/utils/transforms";

interface LotEditDialogProps {
  lot: NonNullable<SupplyChainLot>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function LotEditDialog({ lot, open, onOpenChange, onSuccess }: LotEditDialogProps) {
  const [updateSupplyChainLot] = useMutation(updateSupplyChainLotMutation, {
    refetchQueries: [supplyChainLotQuery, weightedAveragesQuery],
  });
  const { toast } = useToast();

  const onSubmit = async (values: LotFormSchema) => {
    try {
      const result = await updateSupplyChainLot({
        variables: {
          input: {
            supplyChainLot: lot.id,
            status: values.status,
            sexClass: values.sexClass,
            reference: transformForApi.text(values.reference),
            sortNote: nullable(transformForApi.text(values.sortNote || "")),
            sourcePartner: nullable(values.sourcePartner),
            destinationPartner: nullable(values.destinationPartner),
            animalBreeds: values.animalBreeds?.length ? values.animalBreeds : [],
            weightTotalIn: values.weightTotalIn ? values.weightTotalIn : null,
            weightTotalOut: values.weightTotalOut ? values.weightTotalOut : null,
            weightTotalFeedIntake: values.weightTotalFeedIntake ? values.weightTotalFeedIntake : null,
            purchasePrice: values.purchasePrice ? values.purchasePrice : null,
            salePrice: values.salePrice ? values.salePrice : null,
            feedCost: values.feedCost ? values.feedCost : null,
            nonFeedCost: values.nonFeedCost ? values.nonFeedCost : null,
            primeFraction: values.primeFraction ? String(Number(values.primeFraction) / 100) : null,
            choiceFraction: values.choiceFraction ? String(Number(values.choiceFraction) / 100) : null,
            selectFraction: values.selectFraction ? String(Number(values.selectFraction) / 100) : null,
            numTotalIn: values.numTotalIn ? Number(values.numTotalIn) : null,
            numTotalOut: values.numTotalOut ? Number(values.numTotalOut) : null,
            numTotalDead: values.numTotalDead ? Number(values.numTotalDead) : null,
            numTotalRemoved: values.numTotalRemoved ? Number(values.numTotalRemoved) : null,
            dateOpened: values.dateOpened ? toISO(values.dateOpened) : null,
            dateClosed: values.dateClosed ? toISO(values.dateClosed) : null,
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: "Error updating lot",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      const mutationResult = result.data?.updateSupplyChainLot;
      if (!mutationResult?.success || (mutationResult?.errors?.length ?? 0) > 0) {
        toast({
          title: "Error updating lot",
          description: mutationResult?.errors?.length
            ? extractErrorMessages(mutationResult.errors)
            : "The operation failed. Please try again.",
          variant: "destructive",
        });
        return;
      }

      onOpenChange(false);

      toast({
        title: "Success",
        description: `Lot "${values.reference}" updated.`,
        variant: "default",
      });

      onSuccess?.();
    } catch (error) {
      console.error("[LotEdit] Unexpected error:", error);
      toast({
        title: "Error updating lot",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Lot</DialogTitle>
          <DialogDescription className="sr-only">Edit lot details</DialogDescription>
        </DialogHeader>
        <LotForm onSubmit={onSubmit} onCancel={handleCancel} initialValues={mapLotToFormValues(lot)} />
      </DialogContent>
    </Dialog>
  );
}

export const mapLotToFormValues = (lot: NonNullable<SupplyChainLot>): Partial<LotFormSchema> => ({
  sexClass: lot.sexClass ?? undefined,
  status: lot.status ?? undefined,
  reference: lot.reference || "",
  sortNote: lot.sortNote || "",
  sourcePartner: lot.sourcePartner?.id,
  destinationPartner: lot.destinationPartner?.id,
  animalBreeds: lot.animalBreeds?.map((breed) => breed?.id).filter((id): id is string => !!id) || [],
  weightTotalIn: lot.weightTotalIn || "",
  weightTotalOut: lot.weightTotalOut || "",
  weightTotalFeedIntake: lot.weightTotalFeedIntake || "",
  purchasePrice: lot.purchasePrice || "",
  salePrice: lot.salePrice || "",
  feedCost: lot.feedCost || "",
  nonFeedCost: lot.nonFeedCost || "",
  primeFraction: lot.primeFraction ? String(Number(lot.primeFraction) * 100) : "",
  choiceFraction: lot.choiceFraction ? String(Number(lot.choiceFraction) * 100) : "",
  selectFraction: lot.selectFraction ? String(Number(lot.selectFraction) * 100) : "",
  numTotalIn: lot.numTotalIn != null ? String(lot.numTotalIn) : "",
  numTotalOut: lot.numTotalOut != null ? String(lot.numTotalOut) : "",
  numTotalDead: lot.numTotalDead != null ? String(lot.numTotalDead) : "",
  numTotalRemoved: lot.numTotalRemoved != null ? String(lot.numTotalRemoved) : "",
  dateOpened: lot.dateOpened ? toJsDate(lot.dateOpened) : undefined,
  dateClosed: lot.dateClosed ? toJsDate(lot.dateClosed) : undefined,
});

export const updateSupplyChainLotMutation = graphql(`
  mutation UpdateSupplyChainLot($input: UpdateSupplyChainLotInput!) {
    updateSupplyChainLot(input: $input) {
      success
      errors {
        field
        message
      }
      supplyChainLot {
        id
        reference
        sexClass
        status
        dateOpened
        dateClosed
        sortNote
        numTotalIn
        numTotalOut
        numTotalDead
        numTotalRemoved
        weightTotalIn
        weightTotalOut
        weightTotalFeedIntake
        weightAverageIn
        weightAverageOut
        purchasePrice
        salePrice
        feedCost
        nonFeedCost
        primeFraction
        choiceFraction
        selectFraction
        sourcePartner {
          id
          businessName
        }
        destinationPartner {
          id
          businessName
        }
        animalBreeds {
          id
          name
        }
      }
    }
  }
`);
